import React, { useCallback } from "react";
import { ErrorMessage, LoadingIndicator } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { FieldGroup } from "../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../common/components/Form/Layout/SimpleFormRow";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { SharedAddressInput } from "../../../SharedAddressInput";
import { type CountrySelectProps } from "../../../SharedAddressInput/types";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import { getCountryValue, getNameFromCountry } from "../../mappers";
import { AdditionalInstructionsInput } from "../AdditionalInstructionsInput";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { BacsBankDetails } from "./BankDetails";
import {
  ADDITIONAL_INSTRUCTIONS_MAX_LENGTH,
  BacsPaymentPrefInputFields,
  BacsPaymentPrefInputSchema,
  type BacsPaymentPrefInputType,
} from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const BacsAddressCountrySelect: React.FC<CountrySelectProps> = ({
  name,
  className,
  isFull,
  placeholder,
  handleChange,
  label,
}) => (
  <CountrySelectionDropDown
    name={name}
    className={className}
    isFull={isFull}
    placeholder={placeholder}
    handleChange={handleChange}
    getValue={getCountryValue}
    label={label}
  />
);

export const BacsPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName,
}) => {
  const { setPaymentPrefDetail, workflowMode, selectedBankCountry } =
    useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({
    countryCode: "GB",
    paymentPrefDetail,
  });

  const handleFormSubmission = useCallback(
    (values: BacsPaymentPrefInputType) => {
      const bankCountry = selectedBankCountry ?? resolveAndSet({ countryCode: "GB" });
      setPaymentPrefDetail(mapFormStateToContextState(values, bankCountry));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick, selectedBankCountry, resolveAndSet],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.BACS}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "BacsPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={BacsPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BacsBankDetails />
      <FieldGroup title="Account details">
        <SimpleFormRow>
          <FormTextInput
            label="Account holder name"
            isFullWidth
            name={BacsPaymentPrefInputFields.AccountName}
          />
        </SimpleFormRow>
        <MultiInputFormRow>
          <FormTextInput
            label="Account number"
            isFullWidth
            name={BacsPaymentPrefInputFields.AccountNumber}
          />
          <FormTextInput
            label="Confirm account number"
            isFullWidth
            name={BacsPaymentPrefInputFields.ConfirmAccountNumber}
          />
        </MultiInputFormRow>
        <SharedAddressInput
          title="Address"
          CountrySelect={BacsAddressCountrySelect}
          name={BacsPaymentPrefInputFields.Address}
          testIsUSA={(country) => isUSA(getNameFromCountry(country))}
        />
        <SimpleFormRow>
          <AdditionalInstructionsInput
            fieldName={BacsPaymentPrefInputFields.AdditionalInstructions}
            maxLength={ADDITIONAL_INSTRUCTIONS_MAX_LENGTH}
          />
        </SimpleFormRow>
      </FieldGroup>
    </PaymentPrefLayoutForm>
  );
};
